import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Box, 
  Button, 
  CircularProgress, 
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton, 
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper, 
  Tab, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs, 
  TextField,
  Tooltip, 
  Typography,
  GlobalStyles,
  Autocomplete,
  Breadcrumbs
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SettingsIcon from '@mui/icons-material/Settings';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import SendIcon from '@mui/icons-material/Send';
import axios from 'axios';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import FolderIcon from '@mui/icons-material/Folder';
import api from '../services/api';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import FileExplorer from '../pages/FileExplorer';
import { backendUrl } from '../config';

// URL du serveur WebSocket Vosk
const VOSK_WS_URL = backendUrl.replace(/^http/, 'ws') + '/ws/speech';

// Styles globaux pour les animations
const GlobalStylesComponent = () => {
  // Création d'une feuille de style globale
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes pulse {
        0% { opacity: 0.3; }
        50% { opacity: 1; }
        100% { opacity: 0.3; }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  
  return null; // Pas de rendu nécessaire
};

// Cache pour la clé API
let cachedApiKey = null;

// Fonction pour récupérer la clé API
async function getMistralApiKey() {
  // Si la clé est en cache, on la renvoie
  if (cachedApiKey) return cachedApiKey;
  
  try {
    console.log('Récupération de la configuration IA...');
    const response = await fetch(`${backendUrl}/api/config/AI-global`, {
      headers: { 
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Erreur de réponse:', response.status, errorText);
      throw new Error(`Erreur ${response.status} lors de la récupération de la configuration IA`);
    }
    
    const configs = await response.json();
    console.log('Configurations IA reçues:', configs);
    
    // Trouver la configuration Mistral
    const mistralConfig = Array.isArray(configs) 
      ? configs.find(config => config.provider === 'mistral' || config.mistral?.apiKey)
      : null;
    
    if (!mistralConfig) {
      throw new Error('Aucune configuration Mistral trouvée dans la configuration IA');
    }
    
    // Extraire la clé API de la configuration Mistral
    const apiKey = mistralConfig.mistral?.apiKey;
    
    if (!apiKey) {
      throw new Error('Aucune clé API trouvée dans la configuration Mistral');
    }
    
    console.log('Clé API Mistral trouvée');
    
    // Mettre en cache la clé
    cachedApiKey = apiKey;
    return apiKey;
    
  } catch (error) {
    console.error('Erreur getMistralApiKey:', error);
    throw error;
  }
}

// Streaming Ollama response (token by token)
async function proxyIaApiRequest({ target_url, payload, headers = {} }) {
  try {
    // Récupérer la clé API
    const apiKey = await getMistralApiKey();
    
    console.log('Envoi de la requête à:', target_url);
    
    // Préparer les en-têtes avec l'authentification
    const requestHeaders = {
      ...headers,
      'Authorization': `Bearer ${apiKey}`,
      'Content-Type': 'application/json'
    };

    // Faire l'appel à l'API Mistral via le proxy
    const res = await fetch(`${backendUrl}/api/proxy-ia`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({ 
        target_url, 
        payload, 
        headers: requestHeaders
      })
    });
    
    if (!res.ok) {
      const errorText = await res.text();
      console.error('Erreur API:', errorText);
      throw new Error(`Erreur API (${res.status}): ${errorText}`);
    }
    
    return res;
  } catch (error) {
    console.error('Erreur dans proxyIaApiRequest:', error);
    throw error;
  }
}

// Version proxy du streaming Ollama
async function streamOllamaResponse({ prompt, model = 'llama3', aiUrl = 'http://localhost:11434/api/chat', onToken, onDone, onError }) {
  try {
    // Vérification stricte avant l'appel proxy
    const payload = {
      model,
      messages: [{ role: 'user', content: prompt }],
      stream: true
    };
    if (!payload.model || !payload.messages || !Array.isArray(payload.messages) || payload.messages.length === 0) {
      if (onError) onError(new Error('Le prompt ou le modèle IA est manquant.'));
      return;
    }
    const res = await proxyIaApiRequest({
      target_url: aiUrl,
      payload,
      headers: { 'Content-Type': 'application/json' }
    });
    if (!res.body) throw new Error('No response body');
    const reader = res.body.getReader();
    let partial = '';
    const decoder = new TextDecoder();
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      for (const line of chunk.split('\n')) {
        if (!line.trim()) continue;
        try {
          const obj = JSON.parse(line);
          const token = obj.message?.content || obj.response || '';
          if (token) {
            partial += token;
            if (onToken) onToken(token, partial);
          }
        } catch (e) {
          // Ignore parse errors (incomplete JSON)
        }
      }
    }
    if (onDone) onDone(partial);
  } catch (err) {
    if (onError) onError(err);
  }
}

// Configuration de base pour Ollama (adapter si besoin)
const OLLAMA_API_URL = process.env.REACT_APP_OLLAMA_API_URL || 'http://localhost:11434/api/generate';
const OLLAMA_MODEL = process.env.REACT_APP_OLLAMA_MODEL || 'llama3';
const API_URL = backendUrl;

const INPUT_OPTIONS = [
  { value: 'custom', label: 'Texte personnalisé' },
  { value: 'prev_output', label: 'Sortie du prompt précédent' },
  { value: 'file', label: 'Fichier' }
];
const OUTPUT_OPTIONS = [
  { value: 'text', label: 'Texte brut' },
  { value: 'json', label: 'JSON (si possible)' }
];

const IA_MODELS = [
  { label: 'Ollama (local)', value: 'ollama', aiUrl: OLLAMA_API_URL, models: ['llama3', 'mistral', 'gemma', 'phi3', 'mixtral', 'codellama'] },
  { label: 'OpenAI GPT-3.5/4', value: 'openai', aiUrl: process.env.REACT_APP_OPENAI_API_URL || '', models: ['gpt-3.5-turbo', 'gpt-4', 'gpt-4o'] },
  { label: 'Mistral (API)', value: 'mistral', aiUrl: process.env.REACT_APP_MISTRAL_API_URL || '', models: ['mistral-tiny', 'mistral-small', 'mistral-medium'] },
  { label: 'Gemini (Google)', value: 'gemini', aiUrl: process.env.REACT_APP_GEMINI_API_URL || '', models: ['gemini-pro'] }
];

function getUserId() {
  const userStr = window.localStorage.getItem('user');
  if (!userStr) return null;
  try {
    const user = JSON.parse(userStr);
    return user.uuid7 || user.uuid || user.id || null;
  } catch {
    return null;
  }
}

async function loadIaConfigsList() {
  const token = window.localStorage.getItem('token');
  try {
    const res = await api.get(`${API_URL}/api/config/AI-global`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    // On suppose que chaque config a un champ 'name' ou 'key' pour l'affichage
    return (res.data || []).map(cfg => ({
      label: cfg.name || cfg.key || 'API IA',
      value: cfg.key || cfg.name,
      ...cfg
    }));
  } catch {
    return [];
  }
}

// Désactivé : la sauvegarde de config IA globale n'est plus autorisée ici
async function saveIaConfigForUser() {
  return;
}

async function loadIaConfigForUser() {
  const uuid7 = getUserId();
  if (!uuid7) return null;
  const token = window.localStorage.getItem('token');
  try {
    const res = await api.get(`${API_URL}/api/config/AI-global`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const configs = res.data
      .filter(cfg => cfg.key === 'ia_config_global' || cfg.key.startsWith('ia_config_'))
      .map(cfg => {
        try {
          return { ...JSON.parse(cfg.value), key: cfg.key, id: cfg.id };
        } catch {
          return { key: cfg.key, id: cfg.id };
        }
      });
    return configs;
  } catch {
    return null;
  }
}

// Fonction pour charger dynamiquement les modèles Ollama
async function fetchOllamaModels(aiUrl = OLLAMA_API_URL) {
  try {
    const url = aiUrl.replace(/\/generate$/, '/tags');
    // N'appelle pas le backend si le provider cible nécessite un payload non vide
    // (ex: 4styl.com/api/chat refuse les payloads vides)
    if (aiUrl.includes('4styl.com')) return [];
    const res = await proxyIaApiRequest({
      target_url: url,
      payload: {},
      headers: { 'Content-Type': 'application/json' }
    });
    if (!res.ok) throw new Error('Erreur chargement modèles Ollama');
    const data = await res.json();
    if (Array.isArray(data.models)) {
      return data.models.map(m => m.name || m.model || m.tag || m);
    }
    if (Array.isArray(data.tags)) {
      return data.tags.map(t => t.name || t.tag || t);
    }
    return [];
  } catch (e) {
    return [];
  }
}

export default function AgentApp() {
  // Références pour la capture audio
  const audioContextRef = useRef(null);
  const audioStreamRef = useRef(null);
  const audioWorkletNodeRef = useRef(null);
  const processorRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const audioChunksRef = useRef([]);
  const isMounted = useRef(true);
  const fileInputRefs = useRef([]);
  
  // États pour la reconnaissance vocale
  const [activePromptIdx, setActivePromptIdx] = useState(0);
  const [isListening, setIsListening] = useState(false);
  const isListeningRef = useRef(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [socket, setSocket] = useState(null);
  const socketRef = useRef(null);
  
  // État pour le chargement de la configuration
  const [loadingConfig, setConfigLoading] = useState(false);
  
  // État pour la synthèse vocale
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [currentUtterance, setCurrentUtterance] = useState(null);

  // ===== ÉTATS PRINCIPAUX =====
  // Modèle IA par défaut
  const defaultIaModel = 'llama3';
  
  // État pour le modèle IA (défini en premier pour éviter les problèmes d'initialisation)
  const [iaModel, setIaModel] = useState(() => {
    return localStorage.getItem('ia_model') || defaultIaModel;
  });
  
  // État pour la gestion des conversations
  const [conversations, setConversations] = useState(() => {
    const saved = localStorage.getItem('conversations');
    return saved ? JSON.parse(saved) : [];
  });
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  
  // Créer une nouvelle conversation
  const createNewConversation = useCallback(async (name = 'Nouvelle conversation') => {
    const modelToUse = iaModel || defaultIaModel;
    const newConversation = {
      id: `conv_${Date.now()}`,
      name,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      model: modelToUse
    };
    
    setConversations(prev => [newConversation, ...prev]);
    setCurrentConversation(newConversation);
    setMessages([]);
    return newConversation;
  }, [iaModel]);

  // Charger une conversation existante
  const loadConversation = useCallback(async (conversationId) => {
    const conversation = conversations.find(c => c.id === conversationId);
    if (!conversation) return;
    
    // Ici, vous pourriez charger les messages depuis votre base de données
    // const conversationMessages = await fetchMessagesForConversation(conversationId);
    // setMessages(conversationMessages || []);
    
    setCurrentConversation(conversation);
    return conversation;
  }, [conversations]);
  
  // État de chargement pour l'IA
  const [isLoadingResponse, setIsLoadingResponse] = useState(false);
  
  // Gestion des prompts
  const [prompts, setPrompts] = useState([{ 
    input: '', 
    output: '', 
    loading: false, 
    error: '', 
    inputType: 'custom', 
    outputType: 'text', 
    prevPromptIdx: null 
  }]);
  
  // Navigation et onglets
  const [tab, setTab] = useState(0);
  
  // Configuration IA
  const [iaProvider, setIaProvider] = useState(() => {
    return localStorage.getItem('ia_provider') || 'ollama';
  });
  
  const [customApiUrl, setCustomApiUrl] = useState(() => {
    return localStorage.getItem('ia_api_url') || 'http://localhost:11434/api/chat';
  });
  
  const [iaModels, setIaModels] = useState([]);
  const [iaConfigs, setIaConfigs] = useState([]);
  
  // Charger la configuration par défaut
  const loadDefaultConfig = useCallback(() => {
    const defaultModel = localStorage.getItem('ia_model') || defaultIaModel;
    const defaultProvider = localStorage.getItem('ia_provider') || 'ollama';
    
    setIaModel(defaultModel);
    setIaProvider(defaultProvider);
    
    // Trouver l'index de la configuration par défaut
    if (Array.isArray(window.__IA_CONFIGS__) && window.__IA_CONFIGS__.length > 0) {
      const idx = window.__IA_CONFIGS__.findIndex(cfg =>
        (cfg.iaModel === defaultModel || cfg.model === defaultModel) &&
        (cfg.iaProvider === defaultProvider || cfg.provider === defaultProvider)
      );
      if (idx >= 0) {
        setSelectedConfigIdx(idx);
      }
    } else {
      setSelectedConfigIdx(-1);
    }
  }, []);
  
  const [selectedConfigIdx, setSelectedConfigIdx] = useState(-1);
  
  // Gestion des erreurs
  const [error, setError] = useState('');
  
  // ===== ÉTATS D'INTERFACE =====
  // Boîtes de dialogue
  const [explorerOpen, setExplorerOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveExplorerOpen, setSaveExplorerOpen] = useState(false);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  
  // Gestion des fichiers
  const [saveFileName, setSaveFileName] = useState('');
  const [saveFolder, setSaveFolder] = useState('');
  const [saveName, setSaveName] = useState(() => `prompts-${new Date().toISOString().slice(0,19).replace(/[:T]/g,'-')}.json`);
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState('');
  const [explorerPath, setExplorerPath] = useState('/');
  const [saveExplorerPath, setSaveExplorerPath] = useState('/');
  const [explorerFiles, setExplorerFiles] = useState([]);
  const [saveExplorerFiles, setSaveExplorerFiles] = useState([]);
  const [explorerLoading, setExplorerLoading] = useState(false);
  const [explorerError, setExplorerError] = useState('');
  const [saveExplorerLoading, setSaveExplorerLoading] = useState(false);
  const [saveExplorerError, setSaveExplorerError] = useState('');
  const [selectedFilePath, setSelectedFilePath] = useState('');
  
  // Sessions
  const [isSaving, setIsSaving] = useState(false);
  const [savedSessions, setSavedSessions] = useState([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  
  // Configuration avancée
  const [configName, setConfigName] = useState('');
  const [configValid, setConfigValid] = useState(true);
  const [configWarning, setConfigWarning] = useState('');
  const [configSaved, setConfigSaved] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  
  // Modèles Ollama
  const [ollamaModels, setOllamaModels] = useState([]);
  const [ollamaModelLoading, setOllamaModelLoading] = useState(false);
  const promptInputRef = useRef(null);

  const currentInputRef = useRef('');

  // Fonction pour arrêter la capture audio
  const stopAudioCapture = useCallback(() => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }
    
    // Arrêter le processeur audio
    if (processorRef.current) {
      if (processorRef.current.onaudioprocess) {
        processorRef.current.onaudioprocess = null;
      }
      if (processorRef.current.disconnect) {
        processorRef.current.disconnect();
      }
      processorRef.current = null;
    }
    
    // Arrêter l'AudioWorkletNode s'il existe
    if (audioWorkletNodeRef.current) {
      if (audioWorkletNodeRef.current.port) {
        audioWorkletNodeRef.current.port.onmessage = null;
      }
      if (audioWorkletNodeRef.current.disconnect) {
        audioWorkletNodeRef.current.disconnect();
      }
      audioWorkletNodeRef.current = null;
    }
    
    // Arrêter le flux audio
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => {
        track.stop();
        track.enabled = false;
      });
      audioStreamRef.current = null;
    }
    
    // Fermer le contexte audio
    if (audioContextRef.current) {
      if (audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
      audioContextRef.current = null;
    }
    
    setMediaRecorder(null);
  }, [mediaRecorder]);

  // Initialisation et chargement des configurations
  useEffect(() => {
    let isMounted = true;
    
    // Chargement de la configuration utilisateur
    const loadDefaultConfig = async () => {
      try {
        const userId = await getUserId();
        if (userId && isMounted) {
          const config = await loadIaConfigForUser(userId);
          if (config && isMounted) {
            setIaProvider(config.iaProvider || 'ollama');
            setIaModel(config.iaModel || 'llama3');
            setCustomApiUrl(config.customApiUrl || 'http://localhost:11434/api/chat');
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement de la configuration IA:', error);
      }
    };

    // Désactivé : Chargement des modèles Ollama
    const loadModels = async () => {
      // Ne rien faire - le chargement automatique est désactivé
      // Un modèle par défaut est défini dans l'effet de chargement initial
    };

    // Chargement des configurations IA
    const loadIaConfigs = async () => {
      try {
      //const response = await api.get('/api/ia-configs');
       //if (isMounted && response.data) {
        //  setIaConfigs(response.data);
        //}
      } catch (error) {
        console.error('Erreur lors du chargement des configurations IA:', error);
      }
    };

    // Exécution des chargements
    if (isMounted) {
      loadDefaultConfig();
      loadModels();
      loadIaConfigs();
    }

    // Nettoyage
    return () => {
      isMounted = false;
      if (socketRef.current) {
        socketRef.current.close();
      }
      stopAudioCapture();
    };
  }, [iaProvider, customApiUrl, getUserId, loadIaConfigForUser, fetchOllamaModels]);

  useEffect(() => {
    isListeningRef.current = isListening;
  }, [isListening]);

  // Initialisation du WebSocket
  const initWebSocket = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.close();
    }

    const ws = new WebSocket(VOSK_WS_URL);
    socketRef.current = ws;
    setSocket(ws);
    
    // Variables pour gérer la reconnexion
    let reconnectAttempts = 0;
    const maxReconnectAttempts = 5;
    const reconnectDelay = 1000; // 1 seconde
    
    const scheduleReconnect = () => {
      if (reconnectAttempts < maxReconnectAttempts) {
        reconnectAttempts++;
        const delay = reconnectDelay * Math.pow(2, reconnectAttempts); // Backoff exponentiel
        console.log(`Tentative de reconnexion ${reconnectAttempts}/${maxReconnectAttempts} dans ${delay}ms...`);
        setTimeout(initWebSocket, delay);
      } else {
        console.error('Nombre maximum de tentatives de reconnexion atteint');
        setIsConnecting(false);
        setIsListening(false);
      }
    };

    // Gestionnaire pour les messages reçus
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        // Gérer les différents types de messages
        switch (data.type) {
          case 'auth_success':
            if (!data.model_loaded) {
              setIsListening(false);
              setIsConnecting(false);
              setError("Le modèle de reconnaissance vocale n'est pas chargé côté serveur");
              return;
            }
            setIsConnecting(false);
            setIsListening(true);
            reconnectAttempts = 0; // Réinitialiser le compteur de reconnexion
            
            // Envoyer la configuration audio
            try {
              ws.send(JSON.stringify({
                type: 'audio_config',
                sample_rate: 16000,
                timestamp: new Date().toISOString()
              }));
            } catch (e) {
              console.error('Erreur lors de l\'envoi de la configuration audio:', e);
            }
            break;
            
          case 'partial_result':
          case 'final_result':
          case 'transcription':
            if (!data.text) break;
            
            // Mettre à jour uniquement la zone de transcription
            setTranscript(data.text);
            
            // Ne rien faire d'autre, on ne met pas à jour le champ d'entrée
            break;
            
          case 'pong':
          case 'heartbeat':
            // Réponse aux pings, rien à faire
            break;
            
          case 'error':
            console.error('Erreur du serveur:', data.message || data.error || 'Erreur inconnue');
            setIsConnecting(false);
            setIsListening(false);
            break;
            
          case 'config_updated':
            console.log('Configuration audio mise à jour:', data);
            break;
            
          default:
            console.log('Message WebSocket non géré:', data);
        }
      } catch (e) {
        console.error('Erreur lors du traitement du message WebSocket:', e, event.data);
      }
    };

    ws.onopen = () => {
      console.log('Connexion WebSocket établie, envoi des informations d\'authentification...');
      // Envoyer immédiatement l'ID client
      const clientId = `client-${Math.random().toString(36).substr(2, 9)}`;
      try {
        ws.send(JSON.stringify({
          client_id: clientId,
          type: 'auth',
          timestamp: new Date().toISOString(),
          version: '1.0'
        }));
        
        // Démarrer un ping périodique
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({
              type: 'ping',
              timestamp: new Date().toISOString()
            }));
          }
        }, 30000); // Toutes les 30 secondes
        
        // Nettoyer l'intervalle à la fermeture
        ws.addEventListener('close', () => clearInterval(pingInterval));
        
      } catch (e) {
        console.error('Erreur lors de l\'envoi des informations d\'authentification:', e);
      }
    };

    ws.onclose = (event) => {
      console.log(`Déconnecté du serveur WebSocket. Code: ${event.code}, Raison: ${event.reason || 'Inconnue'}`);
      if (isMounted.current) {
        setIsConnecting(false);
        setIsListening(false);
      }
      
      // Ne pas tenter de se reconnecter si c'est une fermeture normale
      if (event.code === 1000) {
        console.log('Fermeture normale de la connexion WebSocket');
        return;
      }
      
      // Tenter de se reconnecter
      if (isMounted.current && isListening) {
        console.log('Tentative de reconnexion...');
        scheduleReconnect();
      }
    };

    ws.onerror = (error) => {
      console.error('Erreur WebSocket:', error);
      if (isMounted.current) {
        setIsConnecting(false);
        setIsListening(false);
      }
    };

    // Le gestionnaire onmessage a été fusionné plus haut dans le code

    return () => ws.close();
  }, [isListening]);

  // Configuration du traitement audio
  const AUDIO_CONFIG = {
    sampleRate: 16000,
    bufferSize: 4096,
    numChannels: 1
  };

  // Fonction pour convertir un Float32Array en Int16Array
  const floatTo16BitPCM = (input) => {
    // Conversion audio en cours
    try {
      const output = new Int16Array(input.length);
      for (let i = 0; i < input.length; i++) {
        const s = Math.max(-1, Math.min(1, input[i]));
        output[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
      }
      // Conversion audio terminée
      return output.buffer;
    } catch (error) {
      console.error('Erreur dans floatTo16BitPCM:', error);
      throw error;
    }
  };

  // Démarrer la capture audio avec AudioWorklet
  const startAudioCapture = useCallback(async () => {
    try {
      // Arrêter tout enregistrement existant
      if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        mediaRecorder.stop();
      }
      
      // Obtenir l'accès au microphone
      console.log('Demande d\'accès au microphone...');
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: { 
          sampleRate: AUDIO_CONFIG.sampleRate,
          channelCount: AUDIO_CONFIG.numChannels,
          echoCancellation: true,
          noiseSuppression: true
        } 
      });
      
      audioStreamRef.current = stream;
      
      // Créer un contexte audio avec la fréquence d'échantillonnage souhaitée
      const audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: AUDIO_CONFIG.sampleRate
      });
      
      // Variables pour le traitement audio
      let audioChunks = [];
      const CHUNK_SIZE = 5; // Nombre de chunks à accumuler avant envoi
      
      try {
        // Charger le processeur audio
        await audioContext.audioWorklet.addModule('/audio-processor.js');
        
        // Créer un nœud AudioWorklet
        const audioWorkletNode = new AudioWorkletNode(audioContext, 'audio-processor');
        
        // Gérer les messages du processeur audio
        audioWorkletNode.port.onmessage = (event) => {
          if (!isListeningRef.current) {
            console.log('Audio ignoré - État:', isListeningRef.current);
            return;
          }
          
          try {
            if (!event.data || !event.data.data) {
              console.warn('Message audio invalide reçu:', event.data);
              return;
            }
            
            // Message audio reçu
            const audioData = new Float32Array(event.data.data);
            
            // Vérifier si nous avons des données audio valides
            const hasAudio = audioData.some(sample => Math.abs(sample) > 0.001);
            
            if (hasAudio) {
              // Ajouter les données au tableau
              audioChunks.push(audioData);
              
              // Envoyer les données par paquets
              if (audioChunks.length >= CHUNK_SIZE) {

                
                // Concaténer tous les chunks
                const totalLength = audioChunks.reduce((sum, arr) => sum + arr.length, 0);
                const concatenated = new Float32Array(totalLength);
                let offset = 0;
                
                audioChunks.forEach(array => {
                  concatenated.set(array, offset);
                  offset += array.length;
                });
                
                // Données audio prêtes pour conversion
                
                // Convertir en 16 bits et envoyer
                // Conversion en PCM 16 bits
                const audioBuffer = floatTo16BitPCM(concatenated);
                
                if (socketRef.current?.readyState === WebSocket.OPEN) {
                  // Envoi des données audio
                  try {
                    socketRef.current.send(audioBuffer);
                    // Données audio envoyées
                  } catch (error) {
                    console.error('Erreur lors de l\'envoi des données audio:', error);
                  }
                } else {
                  console.warn('WebSocket non connecté, impossible d\'envoyer les données audio');
                }
                
                // Réinitialiser le tableau de chunks
                audioChunks = [];
              }
            }
          } catch (error) {
            console.error('Erreur lors du traitement audio:', error);
          }
        };
        
        // Créer un MediaRecorder pour la compatibilité
        const recorder = new MediaRecorder(stream);
        
        // Gérer les erreurs
        recorder.onerror = (event) => {
          console.error('Erreur MediaRecorder:', event);
          stopAudioCapture();
        };
        
        // Connecter le nœud audio
        const source = audioContext.createMediaStreamSource(stream);
        source.connect(audioWorkletNode);
        audioWorkletNode.connect(audioContext.destination);
        
        // Démarrer l'enregistrement
        recorder.start(100);
        
        // Sauvegarder les références pour le nettoyage
        audioContextRef.current = audioContext;
        audioWorkletNodeRef.current = audioWorkletNode;
        setMediaRecorder(recorder);
        
        console.log('Capture audio démarrée avec succès (AudioWorklet)');
        return true;
        
      } catch (error) {
        console.warn('Impossible d\'utiliser AudioWorklet, retour à ScriptProcessorNode:', error);
        // Fallback vers ScriptProcessorNode si AudioWorklet échoue
        return startWithScriptProcessor(audioContext, stream);
      }
      
    } catch (error) {
      console.error('Erreur lors du démarrage de la capture audio:', error);
      stopAudioCapture();
      return false;
    }
  }, [isListening]);

  // Fonction de fallback utilisant ScriptProcessorNode
  const startWithScriptProcessor = useCallback(async (audioContext, stream) => {
    try {
      // Créer un processeur audio (fallback)
      const processor = audioContext.createScriptProcessor(
        AUDIO_CONFIG.bufferSize,
        AUDIO_CONFIG.numChannels,
        AUDIO_CONFIG.numChannels
      );
      
      // Variables pour le traitement audio
      let audioChunks = [];
      const CHUNK_SIZE = 5; // Nombre de chunks à accumuler avant envoi
      
      // Gestionnaire de traitement audio
      processor.onaudioprocess = (e) => {
        if (!isListeningRef.current) return;
        
        try {
          // Récupérer les données audio du canal gauche (mono)
          const inputData = e.inputBuffer.getChannelData(0);
          
          // Vérifier si nous avons des données audio valides
          const hasAudio = inputData.some(sample => Math.abs(sample) > 0.001);
          
          if (hasAudio) {
            // Ajouter les données au tableau
            audioChunks.push(new Float32Array(inputData));
            
            // Envoyer les données par paquets
            if (audioChunks.length >= CHUNK_SIZE) {
              // Concaténer tous les chunks
              const concatenated = new Float32Array(audioChunks.length * audioChunks[0].length);
              let offset = 0;
              audioChunks.forEach(array => {
                concatenated.set(array, offset);
                offset += array.length;
              });
              
              // Convertir en 16 bits et envoyer
              const audioBuffer = floatTo16BitPCM(concatenated);
              
              if (socketRef.current?.readyState === WebSocket.OPEN) {
                socketRef.current.send(audioBuffer);
              }
              
              // Réinitialiser le tableau de chunks
              audioChunks = [];
            }
          }
        } catch (error) {
          console.error('Erreur lors du traitement audio (fallback):', error);
        }
      };
      
      // Créer un MediaRecorder pour la compatibilité
      const recorder = new MediaRecorder(stream);
      
      // Gérer les erreurs
      recorder.onerror = (event) => {
        console.error('Erreur MediaRecorder (fallback):', event);
        stopAudioCapture();
      };
      
      // Connecter le processeur audio
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(processor);
      processor.connect(audioContext.destination);
      
      // Démarrer l'enregistrement
      recorder.start(100);
      
      // Sauvegarder les références pour le nettoyage
      audioContextRef.current = audioContext;
      processorRef.current = processor;
      setMediaRecorder(recorder);
      
      console.log('Capture audio démarrée avec succès (ScriptProcessorNode fallback)');
      return true;
      
    } catch (error) {
      console.error('Erreur lors du démarrage de la capture audio (fallback):', error);
      stopAudioCapture();
      return false;
    }
  }, [isListening]);

  // Bascule du mode écoute
  const toggleListening = useCallback(async () => {
    if (isListening) {
      // Arrêter l'écoute
      console.log('Arrêt de la reconnaissance vocale...');
      setIsListening(false);
      await stopAudioCapture();
      
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        try {
          socketRef.current.send(JSON.stringify({ eof: true }));
          console.log('Signal de fin de flux envoyé au serveur');
        } catch (error) {
          console.error('Erreur lors de l\'envoi du signal de fin de flux:', error);
        }
      }
      
      console.log('Reconnaissance vocale arrêtée');
    } else {
      // Démarrer l'écoute
      console.log('Démarrage de la reconnaissance vocale...');
      setIsListening(true);
      setIsConnecting(true);
      setTranscript('');
      
      try {
        await initWebSocket();
        await startAudioCapture();
      } catch (error) {
        console.error('Erreur lors du démarrage de la reconnaissance vocale:', error);
        setIsListening(false);
        setIsConnecting(false);
      }
    }
  }, [
    isListening, 
    initWebSocket, 
    startAudioCapture, 
    stopAudioCapture, 
    setIsListening, 
    setIsConnecting, 
    setTranscript
  ]);

  // Gérer l'envoi automatique de la transcription après un délai d'inactivité
  const transcriptTimeoutRef = useRef(null);
  
  useEffect(() => {
    if (transcript) {
      console.log('Transcription mise à jour:', transcript);
      
      // Réinitialiser le timeout existant
      if (transcriptTimeoutRef.current) {
        clearTimeout(transcriptTimeoutRef.current);
      }
      
      // Si l'utilisateur est en train de parler, on attend 2 secondes d'inactivité
      if (isListening) {
        transcriptTimeoutRef.current = setTimeout(() => {
          console.log('Délai d\'inactivité atteint, envoi de la transcription');
          handleSendTranscription();
        }, 2000); // 2 secondes d'inactivité
      }
    }
    
    // Nettoyer le timeout lors du démontage
    return () => {
      if (transcriptTimeoutRef.current) {
        clearTimeout(transcriptTimeoutRef.current);
      }
    };
  }, [transcript, isListening]);

  // Fonction pour arrêter la lecture en cours
  const stopSpeaking = useCallback(() => {
    if (currentUtterance) {
      currentUtterance.pause();
      currentUtterance.currentTime = 0;
      setCurrentUtterance(null);
    }
    setIsSpeaking(false);
  }, [currentUtterance]);

  // Fonction pour utiliser la synthèse vocale du serveur
  const speakUsingSystemTTS = async (text) => {
    if (!text) return false;
    
    // Limiter la taille du texte pour éviter les problèmes de découpage
    const MAX_CHUNK_LENGTH = 100; // Nombre de caractères par morceau
    
    // Fonction pour diviser le texte en phrases complètes
    const splitIntoSentences = (text) => {
      // Utiliser une expression régulière pour diviser sur les ponctuations de fin de phrase
      // mais en conservant les guillemets et parenthèses
      const sentenceRegex = /([^.!?]+[.!?]+\s*|"[^"]*"|\([^)]*\))/g;
      const sentences = [];
      let match;
      
      // Récupérer toutes les phrases
      while ((match = sentenceRegex.exec(text)) !== null) {
        // Nettoyer les espaces en début et fin de phrase
        const sentence = match[0].trim();
        if (sentence) {
          sentences.push(sentence);
        }
      }
      
      // Si on n'a pas trouvé de ponctuation, diviser par virgules
      if (sentences.length === 0) {
        return text.split(/(?<=,)/g).map(s => s.trim()).filter(Boolean);
      }
      
      return sentences;
    };
    
    // Fonction pour diviser les phrases trop longues
    const splitLongSentences = (sentences) => {
      const result = [];
      
      for (const sentence of sentences) {
        if (sentence.length <= MAX_CHUNK_LENGTH) {
          result.push(sentence);
        } else {
          // Diviser la phrase en morceaux plus petits sur les virgules
          const parts = sentence.split(/(?<=[,;:])/g);
          let currentChunk = '';
          
          for (const part of parts) {
            if ((currentChunk + part).length <= MAX_CHUNK_LENGTH) {
              currentChunk += part;
            } else {
              if (currentChunk) {
                result.push(currentChunk);
                currentChunk = part;
              } else {
                // Si le morceau est plus long que MAX_CHUNK_LENGTH, on le coupe quand même
                result.push(part.substring(0, MAX_CHUNK_LENGTH));
                currentChunk = part.substring(MAX_CHUNK_LENGTH);
              }
            }
          }
          
          if (currentChunk) {
            result.push(currentChunk);
          }
        }
      }
      
      return result;
    };
    
    // Diviser le texte en phrases
    const sentences = splitIntoSentences(text);
    // Diviser les phrases trop longues
    const chunks = splitLongSentences(sentences);
    
    console.log('Text chunks for TTS:', chunks);
    
    setIsSpeaking(true);
    
    try {
      // Lire chaque morceau à la suite
      for (const chunk of chunks) {
        if (!chunk.trim()) continue;
        
        const token = window.localStorage.getItem('token');
        const response = await fetch(`${backendUrl}/api/tts/tts`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify({
            text: chunk,
            lang: 'fr'
          })
        });
        
        if (!response.ok) {
          throw new Error(`Erreur HTTP: ${response.status}`);
        }
        
        const audio = new Audio();
        audio.src = URL.createObjectURL(await response.blob());
        setCurrentUtterance(audio);
        
        // Attendre que la lecture du morceau soit terminée
        await new Promise((resolve) => {
          audio.onended = resolve;
          audio.onerror = (e) => {
            console.error('Erreur de lecture audio:', e);
            resolve(); // Continuer avec le morceau suivant même en cas d'erreur
          };
          
          audio.play().catch(e => {
            console.error('Erreur de lecture audio:', e);
            resolve(); // Continuer avec le morceau suivant même en cas d'erreur
          });
        });
      }
      
      return true;
    } catch (error) {
      console.error('Erreur avec la synthèse vocale serveur:', error);
      return false;
    } finally {
      setIsSpeaking(false);
      setCurrentUtterance(null);
    }
  };

  // Fonction pour lire un texte à l'aide de la synthèse vocale du serveur
  const speak = useCallback(async (text) => {
    if (!text) {
      console.warn('Aucun texte à lire');
      return false;
    }
    
    // Arrêter toute lecture en cours
    stopSpeaking();
    
    // Toujours utiliser le backend pour la synthèse vocale
    return await speakUsingSystemTTS(text);
  }, [stopSpeaking, speakUsingSystemTTS]);

  // Fonction pour envoyer la transcription
  const handleSendTranscription = useCallback(() => {
    console.log('handleSendTranscription appelé avec transcript:', transcript);
    if (!transcript) {
      console.log('Aucune transcription à envoyer');
      return;
    }

    const currentIdx = activePromptIdx ?? 0;
    const textToSend = transcript.trim();
    
    // Créer une copie locale des fonctions nécessaires
    const sendPrompt = handleSendPrompt;
    const sendPromptsCascade = handleSendPromptsCascade;
    
    // Créer une fonction pour envoyer le prompt
    const sendPromptWithText = () => {
      console.log('Envoi du prompt avec le texte:', textToSend);
      
      // Mettre à jour l'interface utilisateur AVANT d'envoyer la requête
      setPrompts(prevPrompts => {
        const newPrompts = [...prevPrompts];
        newPrompts[currentIdx] = {
          ...newPrompts[currentIdx],
          input: textToSend,
          output: '',
          loading: true,
          error: '',
          fromVoice: true // Marquer explicitement que ce prompt vient d'une commande vocale
        };
        console.log('Prompt mis à jour avec fromVoice=true');
        return newPrompts;
      });

      // Utiliser la référence actuelle de l'état des prompts
      const currentPrompts = prompts;
      
      // Petite pause pour s'assurer que l'état est mis à jour
      setTimeout(() => {
        if (currentIdx === currentPrompts.length - 1) {
          console.log('Appel de sendPromptsCascade avec fromVoice=true');
          // S'assurer que le prompt est bien marqué comme venant de la voix
          const updatedPrompts = [...currentPrompts];
          updatedPrompts[currentIdx] = {
            ...updatedPrompts[currentIdx],
            fromVoice: true
          };
          setPrompts(updatedPrompts);
          sendPromptsCascade(currentIdx, textToSend);
        } else {
          console.log('Appel de sendPrompt avec fromVoice=true');
          // Pour sendPrompt, on doit passer l'information via un état partagé
          // car la fonction ne prend pas de paramètre fromVoice
          const updatedPrompts = [...currentPrompts];
          updatedPrompts[currentIdx] = {
            ...updatedPrompts[currentIdx],
            fromVoice: true
          };
          setPrompts(updatedPrompts);
          sendPrompt(currentIdx, textToSend);
        }
      }, 100);
    };

    // Démarrer l'envoi
    sendPromptWithText();
    
    // Arrêter l'écoute
    setIsListening(false);
    stopAudioCapture();
  }, [transcript, activePromptIdx, stopAudioCapture, handleSendPrompt, handleSendPromptsCascade, prompts.length]);

  // Chargement automatique de la config IA par défaut utilisateur
  useEffect(() => {
    let isMounted = true;
    
    const loadDefaultConfig = async () => {
      const userStr = window.localStorage.getItem('user');
      let uuid7 = null;
      
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          uuid7 = user.uuid7 || user.uuid || user.id || null;
        } catch (e) {
          console.error('Erreur lors de l\'analyse des informations utilisateur:', e);
          return;
        }
      }
      
      if (!uuid7) return;
      
      const token = window.localStorage.getItem('token');
      
      try {
        const response = await fetch(`${backendUrl}/api/config/AI-default/${uuid7}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        
        if (!response.ok) throw new Error('Erreur lors du chargement de la configuration');
        
        const cfg = await response.json();
        
        if (!isMounted) return;
        
        if (cfg.provider) {
          setIaProvider(cfg.provider);
          localStorage.setItem('ia_provider', cfg.provider);
        }
        if (cfg.model) {
          setIaModel(cfg.model);
          localStorage.setItem('ia_model', cfg.model);
        }
        if (cfg.endpoint !== undefined) {
          setCustomApiUrl(cfg.endpoint);
          localStorage.setItem('ia_api_url', cfg.endpoint || '');
        }
        
        // Synchroniser la sélection dans la liste déroulante si présente
        const configs = iaConfigs;
        if (Array.isArray(configs) && configs.length > 0) {
          const idx = configs.findIndex(cfg =>
            (cfg.iaModel === cfg.model || cfg.model === cfg.model) &&
            (cfg.iaProvider === cfg.provider || cfg.provider === cfg.provider)
          );
          if (idx >= 0 && isMounted) {
            setSelectedConfigIdx(idx);
          }
        } else {
          setSelectedConfigIdx(-1);
        }
      } catch (error) {
        console.error('Erreur lors du chargement de la configuration par défaut:', error);
      }
    };
    
    loadDefaultConfig();
    
    return () => {
      isMounted = false;
    };
  }, [iaConfigs]);

  // Les références sont maintenant définies en haut du composant

  async function fetchSaveExplorerFiles(path) {
    setSaveExplorerLoading(true);
    setSaveExplorerError('');
    try {
      const userStr = window.localStorage.getItem('user');
      let uuid7 = null;
      if (userStr) {
        try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
      }
      if (!uuid7) throw new Error('Utilisateur non trouvé');
      const token = window.localStorage.getItem('token');
      let relPath = path === '/' ? '' : path.replace(/^\//, '');
      const res = await api.get(`${backendUrl}/files?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPath)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSaveExplorerFiles(res.data.filter(f => f.type === 'folder' || f.type === 'file'));
    } catch {
      setSaveExplorerFiles([]);
    } finally {
      setSaveExplorerLoading(false);
    }
  }

  function handleOpenSaveExplorer() {
    setSaveExplorerOpen(true);
    setSaveExplorerPath('/');
    setSaveName(`prompts-${new Date().toISOString().slice(0,19).replace(/[:T]/g,'-')}.json`);
    fetchSaveExplorerFiles('/');
  }

  function handleSaveExplorerNavigate(folder) {
    const newPath = saveExplorerPath === '/' ? `/${folder}` : `${saveExplorerPath}/${folder}`;
    setSaveExplorerPath(newPath);
    fetchSaveExplorerFiles(newPath);
  }

  function handleSaveExplorerGoUp() {
    if (saveExplorerPath === '/') return;
    const parts = saveExplorerPath.split('/').filter(Boolean);
    parts.pop();
    const newPath = parts.length === 0 ? '/' : '/' + parts.join('/');
    setSaveExplorerPath(newPath);
    fetchSaveExplorerFiles(newPath);
  }

  async function handleSavePromptsToBackend() {
    const userStr = window.localStorage.getItem('user');
    let uuid7 = null;
    if (userStr) {
      try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
    }
    if (!uuid7) { alert('Utilisateur non trouvé'); return; }
    const token = window.localStorage.getItem('token');
    let relPath = saveExplorerPath === '/' ? saveName : saveExplorerPath.replace(/^\//, '') + '/' + saveName;
    try {
      await api.post(`${backendUrl}/save-file`, {
        uuid7,
        path: relPath,
        content: JSON.stringify(prompts, null, 2),
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSaveExplorerOpen(false);
      alert('Session de prompts sauvegardée !');
    } catch (e) {
      alert("Erreur lors de la sauvegarde des prompts : " + (e?.message || ''));
    }
  }

  async function fetchExplorerFiles(path) {
    setExplorerLoading(true);
    setExplorerError('');
    try {
      const userStr = window.localStorage.getItem('user');
      let uuid7 = null;
      if (userStr) {
        try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
      }
      if (!uuid7) throw new Error('Utilisateur non trouvé');
      const token = window.localStorage.getItem('token');
      let relPath = path === '/' ? '' : path.replace(/^\//, '');
      const res = await api.get(`${backendUrl}/files?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPath)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExplorerFiles(res.data);
    } catch (e) {
      setExplorerError("Erreur lors du chargement des fichiers.");
      setExplorerFiles([]);
    } finally {
      setExplorerLoading(false);
    }
  }

  function handleOpenFromExplorer() {
    setExplorerOpen(true);
    setExplorerPath('/');
    fetchExplorerFiles('/');
  }

  async function handleExplorerSelectFile(file) {
    // Lecture backend
    try {
      setExplorerLoading(true);
      const userStr = window.localStorage.getItem('user');
      let uuid7 = null;
      if (userStr) {
        try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
      }
      if (!uuid7) throw new Error('Utilisateur non trouvé');
      const token = window.localStorage.getItem('token');
      let relPath = explorerPath === '/' ? file.name : explorerPath.replace(/^\//, '') + '/' + file.name;
      const res = await api.get(`${API_URL}/file?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPath)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const loadedPrompts = JSON.parse(res.data.content);
      if (!Array.isArray(loadedPrompts)) throw new Error('Format de fichier invalide');
      setPrompts(loadedPrompts);
      setExplorerOpen(false);
      alert('Session de prompts chargée !');
    } catch (e) {
      setExplorerError("Erreur lors de la lecture du fichier.");
    } finally {
      setExplorerLoading(false);
    }
  }

  function handleExplorerNavigate(folder) {
    const newPath = explorerPath === '/' ? `/${folder}` : `${explorerPath}/${folder}`;
    setExplorerPath(newPath);
    fetchExplorerFiles(newPath);
  }

  function handleExplorerGoUp() {
    if (explorerPath === '/') return;
    const parts = explorerPath.split('/').filter(Boolean);
    parts.pop();
    const newPath = parts.length === 0 ? '/' : '/' + parts.join('/');
    setExplorerPath(newPath);
    fetchExplorerFiles(newPath);
  }

  function handleOpenExplorer() {
    setExplorerOpen(true);
  }

  function handleExplorerSelectFile(file) {
    // On suppose que file possède .name et .type
    let relPath = explorerPath === '/' ? file.name : explorerPath.replace(/^\//, '') + '/' + file.name;
    setSelectedFilePath(relPath);
    setExplorerOpen(false);
  }

  // Validation de la sélection IA à chaque changement
  useEffect(() => {
    if (!iaProvider || !iaModel) {
      setConfigValid(false);
      setConfigWarning("Sélectionnez un provider et un modèle IA.");
    } else {
      setConfigValid(true);
      setConfigWarning("");
    }
  }, [iaProvider, iaModel]);

  function handleLocalFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFilePath(file.name); // Affiche juste le nom, ou utilisez file.path si disponible (web: non)
    // Pour lire le contenu du fichier local et l'insérer dans le prompt courant :
    const reader = new FileReader();
    reader.onload = function(evt) {
      const content = evt.target.result;
      setPrompts(prompts => {
        const idx = activePromptIdx ?? 0;
        const newPrompts = [...prompts];
        newPrompts[idx] = {
          ...newPrompts[idx],
          input: newPrompts[idx].input ? newPrompts[idx].input + '\n' + content : content
        };
        return newPrompts;
      });
    };
    reader.readAsText(file);
  }

  // Sauvegarder l'historique dans localStorage
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem('messages', JSON.stringify(messages));
    }
  }, [messages]);

  // Fonction pour effacer l'historique de la conversation
  const clearConversation = () => {
    setMessages([]);
    localStorage.removeItem('messages');
    setCurrentConversation(null);
    
    // Créer une nouvelle conversation vide
    createNewConversation('Nouvelle conversation');
    
    // Réinitialiser l'état des prompts
    setPrompts([{
      input: '',
      output: '',
      loading: false,
      error: '',
      inputType: 'custom',
      outputType: 'text',
      prevPromptIdx: null
    }]);
  };

  async function handleSendPrompt(idx, text) {
    if (!text || !text.trim()) return;
    
    // Mettre à jour l'état de chargement
    setIsLoadingResponse(true);
    
    // Ajouter le message de l'utilisateur à l'historique
    const userMessage = { 
      role: 'user', 
      content: text,
      timestamp: new Date().toISOString(),
      conversationId: currentConversation?.id
    };
    
    setMessages(prev => [...prev, userMessage]);
    
    // Mettre à jour l'état des prompts pour l'affichage
    const currentPrompts = [...prompts];
    const isFromVoice = true;
    
    try {
      // Si c'est le dernier prompt, on utilise sendPromptsCascade, sinon sendPrompt
      if (idx === currentPrompts.length - 1) {
        console.log('Appel de sendPromptsCascade avec fromVoice=' + isFromVoice);
        sendPromptsCascade(idx, text);
      } else {
        console.log('Appel de sendPrompt avec fromVoice=' + isFromVoice);
        // Pour sendPrompt, on doit passer l'information via un état partagé
        const updatedPrompts = [...currentPrompts];
        updatedPrompts[idx] = {
          ...updatedPrompts[idx],
          fromVoice: isFromVoice
        };
        setPrompts(updatedPrompts);
        sendPrompt(idx, text);
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
      
      // Mettre à jour l'interface utilisateur avec l'erreur
      const errorPrompts = [...currentPrompts];
      if (errorPrompts[idx]) {
        errorPrompts[idx] = {
          ...errorPrompts[idx],
          loading: false,
          error: 'Erreur lors de l\'envoi du message: ' + (error.message || 'Erreur inconnue')
        };
        setPrompts(errorPrompts);
      }
      
      // Ajouter un message d'erreur à l'historique
      const errorMessage = {
        role: 'system',
        content: 'Une erreur est survenue: ' + (error.message || 'Erreur inconnue'),
        timestamp: new Date().toISOString(),
        isError: true,
        conversationId: currentConversation?.id
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoadingResponse(false);
    }
    
    // Configuration de l'IA
    let provider = iaProvider;
    let model = iaModel;
    let aiUrl = customApiUrl;
    
    if (iaConfigs && iaConfigs.length > 0 && typeof selectedConfigIdx === 'number' && 
        selectedConfigIdx >= 0 && iaConfigs[selectedConfigIdx]) {
      const cfg = iaConfigs[selectedConfigIdx];
      provider = cfg.iaProvider || cfg.provider || provider;
      model = cfg.iaModel || cfg.model || model;
      aiUrl = cfg.customApiUrl || cfg.aiUrl || aiUrl;
    }

    // Vérification stricte avant envoi
    if (!model || typeof model !== 'string' || !model.trim()) {
      setPrompts(prompts => {
        const newPrompts = [...prompts];
        newPrompts[idx].error = 'Aucun modèle IA sélectionné.';
        newPrompts[idx].loading = false;
        return newPrompts;
      });
      return;
    }
    if (!aiUrl || typeof aiUrl !== 'string' || !/^https?:\/\//.test(aiUrl)) {
      setPrompts(prompts => {
        const newPrompts = [...prompts];
        newPrompts[idx].error = "L'URL de l'API IA est invalide ou manquante.";
        newPrompts[idx].loading = false;
        return newPrompts;
      });
      return;
    }

    setPrompts(prompts => {
      const newPrompts = [...prompts];
      newPrompts[idx].output = '';
      newPrompts[idx].loading = true;
      newPrompts[idx].error = '';
      return newPrompts;
    });
    let prompt = text !== undefined ? text : prompts[idx].input;
    const prevIdx = prompts[idx].prevPromptIdx;
    if (typeof prevIdx === 'number' && prevIdx >= 0 && prevIdx < idx) {
      const prevPrompt = prompts[prevIdx].input;
      prompt = prevPrompt + '\n' + prompt;
    }
    try {
      setPrompts(prompts => {
        const newPrompts = [...prompts];
        newPrompts[idx].loading = true;
        newPrompts[idx].error = '';
        return newPrompts;
      });
      // Créer une nouvelle conversation si nécessaire
      if (!currentConversation) {
        await createNewConversation(`Conversation - ${new Date().toLocaleString()}`);
      }

      // Préparer le message utilisateur
      const userMessage = {
        id: `msg_${Date.now()}`,
        role: 'user',
        content: prompt,
        timestamp: new Date().toISOString(),
        conversationId: currentConversation?.id
      };

      // Mettre à jour l'interface utilisateur immédiatement
      setMessages(prev => [...prev, userMessage]);
      
      // Préparer le contexte pour l'API Ollama
      const formatMessage = (msg) => ({
        role: msg.role,
        content: msg.content,
        ...(msg.images && { images: msg.images })
      });

      // Préparer le contexte de la conversation
      const allMessages = [];
      
      // Ajouter le message système s'il existe
      const systemMessage = messages.find(m => m.role === 'system');
      if (systemMessage) {
        allMessages.push(formatMessage(systemMessage));
      }
      
      // Ajouter l'historique complet des messages (sauf le message utilisateur actuel)
      const previousMessages = messages
        .filter(m => ['user', 'assistant'].includes(m.role) && m.content !== text)
        .slice(-10) // Limiter à 10 derniers messages pour le contexte
        .map(formatMessage);
      
      // Ajouter le message actuel
      const currentUserMessage = {
        role: 'user',
        content: prompt
      };
      
      // Combiner tous les messages
      const messagesToSend = [...allMessages, ...previousMessages, currentUserMessage];
      
      console.log('Messages envoyés à l\'API:', JSON.stringify(messagesToSend, null, 2));
      
      console.log('Contexte de la conversation envoyé à Ollama:', {
        messages: messages.length,
        estimatedTokens: tokenCount,
        hasSystemMessage: !!systemMessage
      });
      
      // Créer le payload pour l'API Ollama
      const payload = {
        model: iaModel,
        messages: messagesToSend,
        stream: false,
        keep_alive: '30m',
        options: {
          num_ctx: 4096,
          temperature: 0.7,
          repeat_penalty: 1.1,
          top_p: 0.9,
          top_k: 40
        }
      };
      
      console.log('Payload avec contexte:', JSON.stringify(payload, null, 2));
      
      // Configurer l'URL cible
      let modelConfig = iaConfigs && iaConfigs.find(cfg => {
        const providerMatch = (cfg.iaProvider || cfg.provider) === provider;
        const modelMatch = [cfg.iaModel, cfg.model, cfg.name].includes(model);
        return providerMatch && modelMatch;
      });
      
      let targetUrl = modelConfig?.endpoint || 
                     (provider === 'ollama' ? aiUrl.replace(/\/generate$/, '/chat') : aiUrl);
      
      console.log('Envoi de la requête à:', targetUrl);
      
      // Envoyer la requête
      const res = await proxyIaApiRequest({
        target_url: targetUrl,
        payload,
        headers: { 'Content-Type': 'application/json' }
      });
      
      let output = '';
      const contentType = res.headers.get('content-type') || '';
      
      if (contentType.includes('application/json')) {
        const data = await res.json();
        output = data.choices?.[0]?.message?.content || 
                data.candidates?.[0]?.content || 
                data.generated_text || 
                data.message?.content || 
                data.response || 
                data.message || 
                JSON.stringify(data);
      } else {
        const text = await res.text();
        try {
          const data = JSON.parse(text);
          output = data.choices?.[0]?.message?.content || 
                  data.candidates?.[0]?.content || 
                  data.generated_text || 
                  data.message?.content || 
                  data.response || 
                  data.message || 
                  text;
        } catch {
          output = text;
        }
      }
      
      console.log('Réponse reçue pour le prompt:', output.substring(0, 100) + '...');
      
      // Mettre à jour l'état des prompts avec la réponse
      setPrompts(prompts => {
        const newPrompts = [...prompts];
        newPrompts[idx] = {
          ...newPrompts[idx],
          output: output,
          loading: false
        };
        console.log('Mise à jour du prompt avec la réponse:', {
          idx,
          outputLength: output?.length,
          fromVoice: newPrompts[idx].fromVoice
        });
        return newPrompts;
      });
      
      // Ajouter la réponse de l'IA à la conversation
      if (output && output.trim()) {
        const assistantMessage = { 
          role: 'assistant', 
          content: output,
          timestamp: new Date().toISOString(),
          conversationId: currentConversation?.id
        };
        
        setMessages(prev => [...prev, assistantMessage]);
        
        // Ici, vous pourriez sauvegarder les messages dans votre base de données
        // await saveMessage(assistantMessage);
        
        console.log('Réponse de l\'IA ajoutée à la conversation:', {
          conversationId: currentConversation?.id,
          contentLength: output.length
        });
      }
      
      // Récupérer l'état actuel du prompt pour éviter les problèmes de fermeture
      const currentPrompts = [...prompts];
      const currentPrompt = currentPrompts[idx] || {};
      const isFromVoice = currentPrompt.fromVoice === true;
      
      console.log('Décision de lecture:', {
        isFromVoice,
        isListening,
        hasTranscript: !!(transcript && transcript.length > 0),
        hasOutput: !!(output && output.trim().length > 0),
        promptIdx: idx,
        promptFromVoice: currentPrompt.fromVoice
      });
      
      if (isFromVoice && output && output.trim().length > 0) {
        console.log('Démarrage de la lecture vocale...');
        
        // Lire la réponse à voix haute
        const speakSuccess = speak(output);
        console.log('Résultat de la lecture vocale:', speakSuccess);
        
        // Réinitialiser le drapeau après utilisation, mais seulement si la lecture a démarré
        if (speakSuccess) {
          setPrompts(prevPrompts => {
            const newPrompts = [...prevPrompts];
            if (newPrompts[idx]) {
              newPrompts[idx] = { ...newPrompts[idx], fromVoice: false };
            }
            return newPrompts;
          });
        }
      } else {
        console.log('Lecture vocale non déclenchée. Raisons:', {
          isFromVoice,
          hasOutput: !!(output && output.trim().length > 0)
        });
      }
    } catch (err) {
      setPrompts(prompts => {
        const newPrompts = [...prompts];
        newPrompts[idx].error = err.message || 'Erreur API';
        newPrompts[idx].loading = false;
        return newPrompts;
      });
    }
  }

  async function handleSendPromptsCascade(idx, text) {
    console.log('handleSendPromptsCascade - idx:', idx, 'text:', text);
    
    // Récupérer l'état actuel des prompts pour éviter les problèmes de fermeture
    const currentPrompts = [...prompts];
    // Toujours forcer fromVoice à true pour déclencher la lecture vocale
    const isFromVoice = true;
    
    // Mettre à jour l'état de chargement
    setIsLoadingResponse(true);
    
    // Ajouter le message de l'utilisateur à l'historique
    if (text && text.trim()) {
      const userMessage = { 
        role: 'user', 
        content: text,
        timestamp: new Date().toISOString(),
        conversationId: currentConversation?.id
      };
      setMessages(prev => [...prev, userMessage]);
    }
    
    console.log('handleSendPromptsCascade - isFromVoice:', isFromVoice);
    
    for (let i = 0; i <= idx; i++) {
      console.log('Traitement du prompt', i, 'de', idx);
      
      // Si déjà une réponse, on saute sauf si loading ou input changé
      if (currentPrompts[i]?.loading) {
        console.log('Prompt', i, 'déjà en cours de chargement, on passe au suivant');
        continue;
      }
      
      // Mettre à jour l'état de chargement en conservant le drapeau fromVoice
      setPrompts(prevPrompts => {
        const newPrompts = [...prevPrompts];
        newPrompts[i] = {
          ...newPrompts[i],
          output: '',
          loading: true,
          error: '',
          fromVoice: true // Toujours forcer à true
        };
        console.log('Mise à jour du prompt', i, 'en mode chargement avec fromVoice:', newPrompts[i].fromVoice);
        return newPrompts;
      });
      
      try {
        // Préparer le prompt
        let prompt = i === idx && text !== undefined ? text : prompts[i].input;
        const prevIdx = prompts[i].prevPromptIdx;
        
        if (typeof prevIdx === 'number' && prevIdx >= 0 && prevIdx < i) {
          const prevPrompt = prompts[prevIdx].input;
          prompt = prevPrompt + '\n' + prompt;
        }
        
        console.log('Envoi du prompt', i, ':', prompt.substring(0, 100) + '...');
        
        // Préparer les messages avec contexte
        const contextMessages = [];
        
        // Ajouter le message système s'il existe dans l'historique
        const systemMessage = messages.find(m => m.role === 'system');
        if (systemMessage) {
          contextMessages.push({
            role: 'system',
            content: systemMessage.content
          });
        } else {
          // Ajouter le contexte système par défaut
          contextMessages.push({
            role: 'system',
            content: 'Tu es un assistant conversationnel utile et professionnel. Réponds de manière naturelle et engageante et breve. la réponse doit etre courte et sans emoji'
          });
        }
        
        // Ajouter l'historique des messages précédents
        const previousMessages = messages
          .filter(m => ['user', 'assistant'].includes(m.role) && m.content !== prompt)
          .slice(-10) // Limiter à 10 derniers messages pour le contexte
          .map(msg => ({
            role: msg.role,
            content: msg.content
          }));
        
        // Ajouter le message actuel
        const currentUserMessage = {
          role: 'user',
          content: prompt
        };
        
        // Combiner tous les messages
        const messagesToSend = [...contextMessages, ...previousMessages, currentUserMessage];
        
        console.log('Messages envoyés à l\'API (prompt cascade):', JSON.stringify(messagesToSend, null, 2));
        
        // Préparer la requête
        const payload = {
          model: iaModel,
          messages: messagesToSend,
          stream: false,
          temperature: 0.7,  // Contrôle la créativité (0-2)
          max_tokens: 1000,  // Limite de tokens pour la réponse
          top_p: 0.9,       // Contrôle la diversité
          frequency_penalty: 0.5,  // Réduit la répétition
          presence_penalty: 0.5    // Encourage la diversité des sujets
        };
        
        console.log('Payload avec contexte:', JSON.stringify(payload, null, 2));
        
        // Configurer l'URL cible
        let modelConfig = iaConfigs && iaConfigs.find(cfg => {
          const providerMatch = (cfg.iaProvider || cfg.provider) === iaProvider;
          const modelMatch = [cfg.iaModel, cfg.model, cfg.name].includes(iaModel);
          return providerMatch && modelMatch;
        });
        
        let targetUrl = modelConfig?.endpoint || 
                     (iaProvider === 'ollama' ? customApiUrl.replace(/\/generate$/, '/chat') : customApiUrl);
        
        console.log('Envoi de la requête à:', targetUrl);
        
        // Envoyer la requête
        const res = await proxyIaApiRequest({
          target_url: targetUrl,
          payload,
          headers: { 'Content-Type': 'application/json' }
        });
        
        // Traiter la réponse
        let output = '';
        const contentType = res.headers.get('content-type') || '';
        
        if (contentType.includes('application/json')) {
          const data = await res.json();
          output = data.choices?.[0]?.message?.content || 
                  data.candidates?.[0]?.content || 
                  data.generated_text || 
                  data.message?.content || 
                  data.response || 
                  data.message || 
                  JSON.stringify(data);
        } else {
          const text = await res.text();
          try {
            const data = JSON.parse(text);
            output = data.choices?.[0]?.message?.content || 
                    data.candidates?.[0]?.content || 
                    data.generated_text || 
                    data.message?.content || 
                    data.response || 
                    data.message || 
                    text;
          } catch {
            output = text;
          }
        }
        
        console.log('Réponse reçue pour le prompt', i, ':', output.substring(0, 100) + '...');
        
        // Mettre à jour l'état avec la réponse
        setPrompts(prevPrompts => {
          const newPrompts = [...prevPrompts];
          newPrompts[i] = {
            ...newPrompts[i],
            output: output,
            loading: false
          };
          console.log('Mise à jour du prompt', i, 'avec la réponse');
          return newPrompts;
        });
        
        // Ajouter la réponse de l'assistant à l'historique
        if (output) {
          const assistantMessage = { 
            role: 'assistant', 
            content: output,
            timestamp: new Date().toISOString(),
            conversationId: currentConversation?.id
          };
          setMessages(prev => [...prev, assistantMessage]);
        }
        const currentPrompt = { ...(prompts[i] || {}), fromVoice: true };
        const shouldSpeak = true; // Toujours vrai pour forcer la lecture
        
        console.log('Vérification de la lecture vocale pour le prompt', i, ':', {
          fromVoice: currentPrompt.fromVoice,
          shouldSpeak,
          outputLength: output?.length,
          outputStart: output?.substring(0, 50) + '...',
          promptContent: currentPrompt.input?.substring(0, 50) + '...'
        });
        
        if (shouldSpeak && output) {
          console.log('Démarrage de la lecture vocale pour le prompt', i);
          
          try {
            // Attendre que la lecture soit terminée avant de continuer
            await speak(output);
            console.log('Lecture vocale terminée pour le prompt', i);
          } catch (error) {
            console.error('Erreur lors de la lecture vocale:', error);
          }
          
          // Réinitialiser le drapeau après utilisation
          setPrompts(prevPrompts => {
            const newPrompts = [...prevPrompts];
            if (newPrompts[i]) {
              newPrompts[i] = { ...newPrompts[i], fromVoice: false };
            }
            return newPrompts;
          });
        } else {
          console.log('Lecture vocale non déclenchée pour le prompt', i, {
            shouldSpeak,
            hasOutput: !!output,
            outputLength: output?.length
          });
        }
        
      } catch (error) {
        console.error('Erreur lors du traitement du prompt', i, ':', error);
        setPrompts(prevPrompts => {
          const newPrompts = [...prevPrompts];
          newPrompts[i] = {
            ...newPrompts[i],
            loading: false,
            error: error.message || 'Erreur lors du traitement de la réponse'
          };
          return newPrompts;
        });
      }
    }
  }

  function handleAddPrompt() {
    setPrompts([...prompts, { input: '', output: '', loading: false, error: '', inputType: 'custom', outputType: 'text', prevPromptIdx: null }]);
  }
  function handleRemovePrompt(idx) {
    if (prompts.length === 1) return;
    setPrompts(prompts.filter((_, i) => i !== idx));
  }
  function handleInputChange(idx, value) {
    setPrompts(prompts => prompts.map((p, i) => i === idx ? { ...p, input: value } : p));
  }
  function handleInputTypeChange(idx, value) {
    setPrompts(prompts => prompts.map((p, i) => i === idx ? { ...p, inputType: value } : p));
  }
  function handleOutputTypeChange(idx, value) {
    setPrompts(prompts => prompts.map((p, i) => i === idx ? { ...p, outputType: value } : p));
  }
  function handleOutputToInput(idx) {
    if (prompts[idx].output) {
      handleInputChange(idx + 1, prompts[idx].output);
    }
  }

  function handleIaProviderChange(event, value) {
    if (!value) return;
    setIaProvider(value.value);
    setIaModel(value.models[0]);
    setCustomApiUrl(value.aiUrl || '');
    localStorage.setItem('ia_provider', value.value);
    localStorage.setItem('ia_model', value.models[0]);
    localStorage.setItem('ia_api_url', value.aiUrl || '');
    setConfigSaved(false);
  }
  function handleIaModelChange(event, value) {
    if (!value) return;
    setIaModel(value);
    localStorage.setItem('ia_model', value);
    // Find the selected provider config or IA_MODELS entry
    let aiUrl = '';
    // Try to find in iaConfigs (user configs)
    let config = iaConfigs.find(cfg => cfg.iaModel === value && cfg.iaProvider === iaProvider);
    if (config && config.customApiUrl) {
      aiUrl = config.customApiUrl;
    } else {
      // Fallback: try to find in IA_MODELS (static)
      const staticProvider = IA_MODELS.find(m => m.value === iaProvider);
      if (staticProvider && staticProvider.aiUrl) {
        aiUrl = staticProvider.aiUrl;
      }
    }
    setCustomApiUrl(aiUrl);
    localStorage.setItem('ia_api_url', aiUrl);
    setConfigSaved(false);
  }
  function handleCustomApiUrlChange(e) {
    setCustomApiUrl(e.target.value);
    localStorage.setItem('ia_api_url', e.target.value);
    setConfigSaved(false);
  }

  async function handleSaveConfig(e) {
    e.preventDefault();
    const key = editingKey || (configName ? `ia_config_${configName.replace(/\s+/g, '_').toLowerCase()}` : 'ia_config_global');
    await saveIaConfigForUser({ iaProvider, iaModel, customApiUrl, key });
    setConfigSaved(true);
    setTimeout(() => setConfigSaved(false), 2000);
    setEditingKey(null);
    loadIaConfigsList();
  }

  function handleFileInputChange(idx, e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      handleInputChange(idx, event.target.result);
    };
    reader.readAsText(file);
    e.target.value = '';
  }

  function handleOpenFileDialog(idx) {
    if (fileInputRefs.current[idx]) fileInputRefs.current[idx].click();
  }

  // Ajoute une fonction pour charger une session de prompts depuis un fichier backend
  async function handleLoadPromptsFromFile() {
    const userStr = window.localStorage.getItem('user');
    let uuid7 = null;
    if (userStr) {
      try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
    }
    if (!uuid7) { alert('Utilisateur non trouvé'); return; }
    const token = window.localStorage.getItem('token');
    const path = window.prompt('Nom du fichier à charger ?', 'prompts-YYYY-MM-DD-HH-mm-ss.json');
    if (!path) return;
    try {
      const res = await api.get(`/file?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(path)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const loadedPrompts = JSON.parse(res.data.content);
      if (!Array.isArray(loadedPrompts)) throw new Error('Format de fichier invalide');
      setPrompts(loadedPrompts);
      alert('Session de prompts chargée !');
    } catch (e) {
      alert("Erreur lors du chargement des prompts : " + (e?.message || ''));
    }
  }

  useEffect(() => {
    setConfigLoading(true);
    loadIaConfigsList().then(cfg => {
      setIaConfigs(cfg);
      setConfigLoading(false);
      // Forcer une sélection valide dès l'ouverture si possible
      if (cfg && cfg.length > 0) {
        const firstCfg = cfg[0];
        if (firstCfg.iaProvider || firstCfg.provider) setIaProvider(firstCfg.iaProvider || firstCfg.provider);
        if (firstCfg.iaModel || firstCfg.model || firstCfg.name) setIaModel(firstCfg.iaModel || firstCfg.model || firstCfg.name);
        if (firstCfg.customApiUrl || firstCfg.endpoint) setCustomApiUrl(firstCfg.customApiUrl || firstCfg.endpoint);
      }
    });
  }, []); // eslint-disable-next-line

  // Désactivation du chargement automatique des modèles Ollama
  useEffect(() => {
    // Définir un modèle par défaut
    setOllamaModels(['llama3']);
    setOllamaModelLoading(false);
  }, []);

  const messagesEndRef = useRef(null);
  
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <GlobalStylesComponent />
      <Tabs 
        value={tab} 
        onChange={(_, v) => setTab(v)} 
        sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          flexShrink: 0
        }}
      >
        <Tab icon={<SmartToyIcon />} label="Agent IA" />
        <Tab icon={<SettingsIcon />} label="Configuration" />
      </Tabs>
      
      <Box sx={{ flex: 1, overflow: 'auto' }}>
      {tab === 0 ? (
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* En-tête avec titre et bouton de réinitialisation */}
          <Box sx={{ 
            p: 2, 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: 1,
            borderColor: 'divider',
            flexShrink: 0
          }}>
            <Typography variant="h6" component="h2">Assistant Conversationnel</Typography>
            <Button 
              size="small" 
              onClick={clearConversation}
              startIcon={<ClearAllIcon />}
              color="error"
              variant="outlined"
            >
              Nouvelle conversation
            </Button>
          </Box>
          
          {/* Zone de chat avec historique */}
          <Box sx={{ 
            flex: 1, 
            overflowY: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            bgcolor: 'background.default'
          }}>
            {messages.length === 0 ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                textAlign: 'center',
                color: 'text.secondary'
              }}>
                <ChatBubbleOutlineIcon sx={{ fontSize: 60, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6">Commencez une nouvelle conversation</Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>Posez une question ou dites quelque chose pour commencer</Typography>
              </Box>
            ) : (
              messages.map((message, index) => (
                <Box 
                  key={index} 
                  sx={{
                    alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start',
                    maxWidth: '80%',
                    bgcolor: message.role === 'user' ? 'primary.main' : 'background.paper',
                    color: message.role === 'user' ? 'primary.contrastText' : 'text.primary',
                    p: 2,
                    borderRadius: 2,
                    boxShadow: 1,
                    position: 'relative',
                    border: message.isError ? '1px solid' : 'none',
                    borderColor: message.isError ? 'error.main' : 'transparent'
                  }}
                >
                  {message.isError && (
                    <ErrorOutlineIcon 
                      color="error" 
                      sx={{ 
                        position: 'absolute', 
                        top: 4, 
                        right: 4,
                        fontSize: '1rem'
                      }} 
                    />
                  )}
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {message.content}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: 'block', 
                      mt: 0.5, 
                      textAlign: 'right',
                      opacity: 0.7,
                      color: message.role === 'user' ? 'primary.contrastText' : 'text.secondary'
                    }}
                  >
                    {new Date(message.timestamp).toLocaleTimeString()}
                  </Typography>
                </Box>
              ))
            )}
            <div ref={messagesEndRef} />
          </Box>
          
          {/* Zone de saisie */}
          <Box sx={{ 
            p: 2, 
            borderTop: '1px solid', 
            borderColor: 'divider',
            flexShrink: 0
          }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Tooltip title={isListening ? 'Arrêter la dictée vocale' : 'Démarrer la dictée vocale'}>
                <IconButton
                  onClick={toggleListening}
                  color={isListening ? 'error' : 'default'}
                  disabled={isConnecting}
                  sx={{
                    border: '1px solid',
                    borderColor: isListening ? 'error.main' : 'divider',
                    '&:hover': {
                      backgroundColor: isListening ? 'error.light' : 'action.hover',
                    },
                  }}
                >
                  {isListening ? (
                    <MicOffIcon sx={{ color: 'error.main' }} />
                  ) : (
                    <MicIcon />
                  )}
                </IconButton>
              </Tooltip>
              {isConnecting && <CircularProgress size={24} />}
              {isListening && (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  bgcolor: 'error.light',
                  color: 'error.contrastText',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  mr: 1
                }}>
                  <Box sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: 'error.main',
                    mr: 1,
                    animation: 'pulse 1.5s infinite'
                  }} />
                  <Typography variant="caption" sx={{ fontWeight: 'medium' }}>
                    En écoute...
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
                <Typography variant="subtitle2">Configuration utilisée :</Typography>
                <Typography variant="body1" color="primary" sx={{ fontWeight: 600, ml: 1 }}>
                  {iaProvider && iaModel ? `${iaProvider} (${iaModel})` : 'Aucune configuration'}
                </Typography>
              </Box>
            </Box>
            
            {/* Indicateur de lecture vocale */}
            {isSpeaking && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                bgcolor: 'info.light',
                color: 'info.contrastText',
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                mb: 1,
                mt: 2
              }}>
                <Box sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'info.main',
                  mr: 1,
                  animation: 'pulse 1.5s infinite'
                }} />
                <Typography variant="caption" sx={{ fontWeight: 'medium' }}>
                  Lecture en cours...
                </Typography>
                <Button 
                  size="small" 
                  onClick={stopSpeaking}
                  color="inherit"
                  sx={{ ml: 'auto', minWidth: 100 }}
                >
                  Arrêter
                </Button>
              </Box>
            )}

            </Box>
            
            {/* Affichage de la transcription en temps réel */}
            {transcript && (
              <Paper 
                elevation={1} 
                sx={{ 
                  p: 2, 
                  bgcolor: 'background.paper', 
                  border: '1px solid', 
                  borderColor: 'divider', 
                  borderRadius: 1,
                  mb: 2,
                  mt: 2
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Transcription en direct:
                  </Typography>
                  <Button 
                    variant="contained" 
                    size="small" 
                    onClick={handleSendTranscription}
                    disabled={!transcript}
                    startIcon={<SendIcon />}
                  >
                    Envoyer la transcription
                  </Button>
                </Box>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {transcript}
                </Typography>
              </Paper>
            )}
            
            {/* En-tête de l'agent IA en cascade */}
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <SmartToyIcon color="primary" fontSize="large" />
              <Typography variant="h5">Agent IA en cascade</Typography>
            </Box>
            <Box sx={{ overflowY: 'auto', maxHeight: 480, pr: 1 }}>
              {prompts.map((prompt, idx) => (
                <Box key={idx} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography variant="subtitle2" color="primary">Prompt {idx + 1}</Typography>
                    {prompts.length > 1 && (
                      <Button size="small" color="error" onClick={() => handleRemovePrompt(idx)} startIcon={<DeleteIcon />} sx={{ ml: 1 }}>Supprimer</Button>
                    )}
                  </Box>
                  <Box display="flex" gap={2} mb={1}>
                    <TextField
                      select
                      label="Source de l'entrée"
                      value={prompt.inputType}
                      onChange={e => handleInputTypeChange(idx, e.target.value)}
                      sx={{ minWidth: 180 }}
                    >
                      {INPUT_OPTIONS.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Format de sortie"
                      value={prompt.outputType}
                      onChange={e => handleOutputTypeChange(idx, e.target.value)}
                      sx={{ minWidth: 150 }}
                    >
                      {OUTPUT_OPTIONS.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  {/* Sélection d'un prompt précédent */}
                  {idx > 0 && (
                    <TextField
                      select
                      label="Prompt précédent à combiner"
                      value={prompt.prevPromptIdx ?? ''}
                      onChange={e => {
                        setPrompts(prompts => prompts.map((pp, i) => i === idx ? { ...pp, prevPromptIdx: Number(e.target.value) } : pp));
                      }}
                      sx={{ minWidth: 220, mb: 2 }}
                    >
                      <MenuItem value="">Aucun</MenuItem>
                      {prompts.slice(0, idx).map((pp, j) => (
                        <MenuItem key={j} value={j}>{`Prompt ${j + 1}`}</MenuItem>
                      ))}
                    </TextField>
                  )}
                  {/* Zone de texte "Entrée" + Import fichier texte */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <TextField
                      label="Entrée"
                      value={prompt.input}
                      inputRef={idx === activePromptIdx ? promptInputRef : null}
                      onChange={e => handleInputChange(idx, e.target.value)}
                      fullWidth
                      multiline
                      minRows={2}
                      sx={{ mb: 0 }}
                      disabled={prompt.inputType === 'file'}
                    />
                    {/* Importer un fichier texte */}
                    <input
                      type="file"
                      accept=".txt,text/plain"
                      style={{ display: 'none' }}
                      id={`import-txt-${idx}`}
                      onChange={e => {
                        const file = e.target.files[0];
                        if (!file) return;
                        const reader = new window.FileReader();
                        reader.onload = evt => {
                          handleInputChange(idx, evt.target.result);
                        };
                        reader.readAsText(file);
                        e.target.value = '';
                      }}
                    />
                    <label htmlFor={`import-txt-${idx}`}>
                      <Button variant="outlined" component="span" startIcon={<FolderOpenIcon />}>
                        Importer un fichier texte
                      </Button>
                    </label>
                    {prompt.inputType === 'file' && (
                      <>
                        <input
                          type="file"
                          accept=".txt,.md,.json,.js,.py,.csv,.log,.xml,.html,.css,.yaml,.yml,.ini,.sh,.bat,.c,.cpp,.java,.ts,.tsx,.jsx,.pdf"
                          style={{ display: 'none' }}
                          ref={el => fileInputRefs.current[idx] = el}
                          onChange={e => handleFileInputChange(idx, e)}
                        />
                        <Button onClick={() => handleOpenFileDialog(idx)} startIcon={<FolderOpenIcon />} variant="outlined">
                          Choisir un fichier
                        </Button>
                      </>
                    )}
                  </Box>
                  {/* Bouton envoyer */}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => idx === prompts.length - 1 ? handleSendPromptsCascade(idx) : handleSendPrompt(idx)}
                    disabled={prompts[idx].loading || !configValid}
                    sx={{ mt: 1, mb: 1 }}
                  >
                    {prompts[idx].loading ? <CircularProgress size={20} /> : 'Envoyer'}
                  </Button>
                  {!configValid && (
                    <Typography color="error" fontSize={14} sx={{ mt: 1 }}>{configWarning}</Typography>
                  )}
                  {/* Affichage de la sortie */}
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="secondary">Réponse IA :</Typography>
                    <Paper variant="outlined" sx={{ p: 2, minHeight: 80, bgcolor: '#fff', color: '#222', fontFamily: 'monospace', fontSize: 16 }}>
                      {prompt.loading ? (
                        <span style={{ color: '#888' }}>Réponse en cours...</span>
                      ) : prompt.error ? (
                        <span style={{ color: 'red' }}>{prompt.error}</span>
                      ) : prompt.output ? (
                        <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{prompt.output}</span>
                      ) : (
                        <span style={{ color: '#aaa' }}>Aucune réponse à afficher.</span>
                      )}
                    </Paper>
                    <Button size="small" sx={{ mt: 1 }} onClick={() => handleOutputToInput(idx)}>
                      Utiliser cette sortie comme entrée du prompt suivant
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Button variant="outlined" onClick={() => { setExplorerOpen(true); setActivePromptIdx(idx); }}>
                      Parcourir les fichiers
                    </Button>
                    <Button variant="outlined" onClick={() => { fileInputRefs.current[idx]?.click(); setActivePromptIdx(idx); }}>
                      Sélectionner un fichier local
                    </Button>
                    <input
                      type="file"
                      ref={el => fileInputRefs.current[idx] = el}
                      style={{ display: 'none' }}
                      onChange={handleLocalFileChange}
                    />
                    {selectedFilePath && activePromptIdx === idx && (
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => {
                          setPrompts(prompts => {
                            const newPrompts = [...prompts];
                            newPrompts[idx] = {
                              ...newPrompts[idx],
                              input: newPrompts[idx].input ? newPrompts[idx].input + ' ' + selectedFilePath : selectedFilePath
                            };
                            return newPrompts;
                          });
                        }}
                      >
                        Insérer le chemin
                      </Button>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 2 }}>
              <Button variant="outlined" color="primary" onClick={handleOpenSaveExplorer}>
                Sauvegarder la session de prompts
              </Button>
              <Button variant="outlined" color="secondary" onClick={handleOpenFromExplorer}>
                Ouvrir une session de prompts
              </Button>
            </Box>
            <Button onClick={handleAddPrompt} startIcon={<AddIcon />} variant="outlined" color="secondary">Ajouter un prompt</Button>
          </Box>
        ) : null}
        
        {tab === 1 ? (
          <form onSubmit={handleSaveConfig} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <SettingsIcon color="primary" fontSize="large" />
              <Typography variant="h6">Configuration IA</Typography>
            </Box>
            {loadingConfig ? (
              <Typography color="text.secondary" sx={{ mb: 2 }}>Chargement de la configuration…</Typography>
            ) : null}
            <Autocomplete
              options={iaConfigs}
              value={iaConfigs[selectedConfigIdx] || null}
              onChange={(_, v) => {
                if (!v) return;
                const idx = iaConfigs.findIndex(cfg => cfg.value === v.value);
                setSelectedConfigIdx(idx);
                if (iaConfigs[idx]) {
                  setIaProvider(iaConfigs[idx].iaProvider || iaConfigs[idx].provider || 'ollama');
                  setIaModel(iaConfigs[idx].iaModel || iaConfigs[idx].model || 'llama3');
                  setCustomApiUrl(iaConfigs[idx].customApiUrl || iaConfigs[idx].aiUrl || '');
                }
              }}
              getOptionLabel={opt => opt.label}
              renderInput={params => <TextField {...params} label="Choisir une IA" />}
              sx={{ mb: 2, width: 320 }}
            />
            
            <Button 
              variant="outlined" 
              color="secondary" 
              sx={{ mt: 1 }} 
              onClick={async () => {
                // Récupérer l'UUID7 de l'utilisateur
                const userStr = window.localStorage.getItem('user');
                let uuid7 = null;
                
                if (userStr) {
                  try {
                    const user = JSON.parse(userStr);
                    uuid7 = user.uuid7 || user.uuid || user.id || null;
                  } catch (e) {
                    console.error('Erreur lors de la récupération de l\'utilisateur:', e);
                  }
                }
                
                if (!uuid7) {
                  alert("Impossible de trouver l'identifiant utilisateur (uuid7)");
                  return;
                }
                
                // Construire la config à sauvegarder
                const config = {
                  provider: iaProvider,
                  model: iaModel,
                  endpoint: customApiUrl
                };
                
                const token = window.localStorage.getItem('token');
                try {
                  await api.put(`${API_URL}/api/config/AI-default/${uuid7}`, config, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  setConfigSaved(true);
                  setTimeout(() => setConfigSaved(false), 2000);
                } catch (e) {
                  alert("Erreur lors de la sauvegarde du modèle par défaut");
                }
              }}
            >
              Utiliser comme modèle par défaut
            </Button>
            {configSaved ? <Typography color="success.main" sx={{ mt: 1 }}>Configuration sauvegardée !</Typography> : null}
          </form>
        ) : null}
      </Box>
      <Dialog open={explorerOpen} onClose={() => setExplorerOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Ouvrir une session de prompts</DialogTitle>
        <DialogContent>
          {explorerLoading && <CircularProgress size={24} />}
          {explorerError && <Typography color="error">{explorerError}</Typography>}
          <Breadcrumbs sx={{ mb: 1 }}>
            <Button size="small" onClick={handleExplorerGoUp} disabled={explorerPath === '/'}>↑</Button>
            <Typography variant="caption">{explorerPath}</Typography>
          </Breadcrumbs>
          <List>
            {explorerFiles.map(file => (
              <ListItem button key={file.name} onClick={async () => file.type === 'folder' ? handleExplorerNavigate(file.name) : await handleExplorerSelectFile(file)}>
                <ListItemIcon>{file.type === 'folder' ? <FolderIcon /> : <InsertDriveFileIcon />}</ListItemIcon>
                <ListItemText primary={file.name} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExplorerOpen(false)}>Annuler</Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialog d'exploration pour sauvegarder la session de prompts */}
      <Dialog open={saveExplorerOpen} onClose={() => setSaveExplorerOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Sauvegarder la session de prompts</DialogTitle>
        <DialogContent>
          {saveExplorerLoading && <CircularProgress size={24} />}
          {saveExplorerError && <Typography color="error">{saveExplorerError}</Typography>}
          <Breadcrumbs sx={{ mb: 1 }}>
            <Button size="small" onClick={handleSaveExplorerGoUp} disabled={saveExplorerPath === '/'}>↑</Button>
            <Typography variant="caption">{saveExplorerPath}</Typography>
          </Breadcrumbs>
          <List>
            {saveExplorerFiles.filter(f => f.type === 'folder').map(folder => (
              <ListItem button key={folder.name} onClick={() => handleSaveExplorerNavigate(folder.name)}>
                <ListItemIcon><FolderIcon /></ListItemIcon>
                <ListItemText primary={folder.name} />
              </ListItem>
            ))}
          </List>
          <TextField
            label="Nom du fichier"
            value={saveName}
            onChange={e => setSaveName(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveExplorerOpen(false)}>Annuler</Button>
          <Button onClick={handleSavePromptsToBackend} variant="contained">Sauvegarder</Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialog d'exploration de fichiers */}
      <Dialog open={explorerOpen} onClose={() => setExplorerOpen(false)} maxWidth="md" fullWidth>
        <FileExplorer
          currentUser={null}
          onOpenApp={(_, file) => handleExplorerSelectFile(file)}
        />
      </Dialog>
    </Box>
  );
}
